/**
 * Route copy for `src/routes/dashboard` (keys: `routes.dashboard.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  components: {
    justNow: 'just now',
    minsMinAgo: '{{mins}} min ago',
    hrsHAgo: '{{hrs}} h ago',
    daysDAgo: '{{days}} d ago',
    loading: 'Loading',
    dataUnavailable: 'Data unavailable',
    nothingToShowForNow: 'Nothing to show for now.',
    unavailable: 'unavailable',
  },
  dashboardPage: {
    plusThisWeek: '+{{count}} this week',
    published: 'Published',
    inProgress: 'In progress',
    pending: 'Pending',
    failed: 'Failed',
    cancelled: 'Cancelled',
    scheduled: 'Scheduled',
    draft: 'Draft',
    generated: 'Generated',
    approved: 'Approved',
    helloGivenname: 'Hello {{givenName}} 👋',
    yourCockpitToday: 'Your cockpit — {{today}}.',
    openTheCopilot: '🤖 Open the Copilot',
    dashboards: 'Dashboards',
    content: 'Content',
    thisWk: 'this wk.',
    published2: 'published',
    scheduledCountScheduled: '{{scheduled_count}} scheduled',
    posts: 'Posts',
    success: 'success',
    publishedCountPublishedFailedCountFailed: '{{published_count}} published · {{failed_count}} failed',
    connectedAccounts: 'Connected accounts',
    active: 'active',
    noNetworkConnected: 'No network connected',
    contactsLeads: 'Contacts / Leads',
    vLeads: '{{v}} leads',
    inYourCrm: 'in your CRM',
    aiCredits: 'AI credits',
    vReserved: '{{v}} reserved',
    available: 'available',
    superadminAccount: 'Superadmin account',
    balanceV: 'Balance {{v}}',
    newContent: 'New content',
    newContact: 'New contact',
    newCampaign: 'New campaign',
    openTheCopilot2: 'Open the Copilot',
    salesPipeline: 'Sales pipeline',
    viewThePipeline: 'View the pipeline →',
    dealsV: 'deals · {{v}}',
    noPipelineConfigured: 'No pipeline configured.',
    upcoming7Days: 'Upcoming (7 days)',
    calendar: 'Calendar →',
    noPublicationScheduledThisWeek: 'No publication scheduled this week.',
    engagementAudience30D: 'Engagement & audience (30 d)',
    analytics: 'Analytics →',
    engagements: 'engagements',
    followers: 'followers',
    leadingV: 'Leading: {{v}}',
    analyticsSummaryUnavailable: 'Analytics summary unavailable',
    audienceGrowth: 'Audience growth',
    noAudienceDataYet: 'No audience data yet.',
    seriesUnavailable: 'Series unavailable',
    recentActivity: 'Recent activity',
    history: 'History →',
    postV: 'Post {{v}}',
    noRecentPublishingActivity: 'No recent publishing activity.',
    aiCreditBalance: 'AI credit balance',
    unlimited: 'unlimited',
    balance: 'Balance',
    reserved: 'Reserved',
    available2: 'Available',
    unlimitedCreditsSuperadminAccount: 'Unlimited credits (superadmin account).',
    balanceBelowTheAlertThreshold: 'Balance below the alert threshold.',
    topUp: '⚡ Top up',
    walletUnavailable: 'Wallet unavailable',
    aiInsights: 'AI insights',
    details: 'Details →',
    notEnoughDataYetForInsights: 'Not enough data yet for insights.',
    insightsUnavailable: 'Insights unavailable',
    nextActions: 'Next actions',
    everythingIsUpToDateNice: 'Everything is up to date — nice work!',
    tips: 'Tips',
    planAhead: 'Plan ahead',
    scheduleAWeekOfContentTo: 'Schedule a week of content to keep a steady rhythm.',
    delegateToTheCopilot: 'Delegate to the Copilot',
    askTheAssistantToWriteOr: 'Ask the assistant to write or rephrase your posts.',
    trackYourPerformance: 'Track your performance',
    checkAnalyticsToSpotYourBest: 'Check Analytics to spot your best formats.',
    contacts: 'Contacts',
    openPipeline: 'Open pipeline',
    dealsWon: 'Deals won',
    activities30D: 'Activities · 30 d',
    pipelineValue30D: 'Pipeline value · 30 d',
    analytics2: 'Analytics',
    loadingTheCurve: 'Loading the curve…',
    curveUnavailableForNow: 'Curve unavailable for now.',
    noDataForThePeriod: 'No data for the period.',
    engagement: 'Engagement',
    noData: 'No data.',
    loading: 'Loading…',
    winRate: 'Win rate',
    avgValue: 'Avg. value',
    lostDeals: 'Lost deals',
    silent: 'Silent',
    remindersDue: 'Reminders due',
    noContactYet: 'No contact yet.',
    aiInsightsFollowUps: 'AI insights · follow-ups',
    noFollowUpSuggestedForNow: 'No follow-up suggested for now 🎉',
    aiAnalysis: 'AI analysis…',
    quietForDaysQuietD: 'Quiet for {{days_quiet}} d',
    retry: 'Retry',
    overdueReminders: 'Overdue reminders',
    noOverdueReminder: 'No overdue reminder. 👌',
    dueV: 'Due {{v}}',
    topOpenDeals: 'Top open deals',
    noOpenDeals: 'No open deals.',
    quickActions: 'Quick actions',
    aiAssistant: 'AI assistant',
    yourCopilotWritesSchedulesAndActs: 'Your copilot writes, schedules and acts on your data in natural language.',
    openTheCopilot3: 'Open the copilot',
    aiAssistantWorkspace: 'AI Assistant workspace',
    upcomingReminders: 'Upcoming reminders',
    nothingPlannedThisWeek: 'Nothing planned this week.',
    topUp2: 'Top up',
    balanceUnavailable: 'Balance unavailable.',
    creditsAvail: 'credits avail.',
    reservedV: 'Reserved {{v}}',
    unlimitedCreditsSuperadminAccount2: 'Unlimited credits (superadmin account)',
    totalBalanceV: 'Total balance {{v}}',
    schedule: 'Schedule',
    topUpYourAiCredits: 'Top up your AI credits',
    availableBalanceVBelowTheAlert: 'Available balance ({{v}}) below the alert threshold.',
    connectASocialNetwork: 'Connect a social network',
    noConnectedAccountLinkANetwork: 'No connected account — link a network to publish.',
    failedCountFailedPublicationS: '{{failed_count}} failed publication(s)',
    retryThemFromThePublishingHistory: 'Retry them from the publishing history.',
    planYourWeek: 'Plan your week',
    noPublicationScheduledInTheNext: 'No publication scheduled in the next 7 days.',
    pendingReviewCountContentItemS: '{{pending_review_count}} content item(s) to review',
    contentIsWaitingForReviewBefore: 'Content is waiting for review before publishing.',
    addYourFirstDeals: 'Add your first deals',
    thePipelinePipelineIsEmpty: 'The “{{pipeline}}” pipeline is empty.',
    unlimited2: 'Unlimited',
    runningGoals: 'Running goals',
    manageGoals: 'Manage →',
  },
  data: {
    leadingPlatformV: 'Leading platform: {{v}}',
    focusYourNextContentWhereYour: 'Focus your next content where your audience reacts the most.',
    averageEngagementPerpostInteractionSPost: 'Average engagement: {{perPost}} interaction(s) / post',
    goodEngagementLevelOverTheLast: 'Good engagement level over the last 30 days.',
    tryMoreInteractiveFormatsToRevive: 'Try more interactive formats to revive engagement.',
    followerGrowthVFollowerGrowth: 'Follower growth: {{v}}{{follower_growth}}',
    totalPostsPostsAnalyzedOver30: '{{total_posts}} posts analyzed over 30 days.',
  },
  widgets: {
    loading: 'Loading…',
    changeVsPreviousPeriod: 'Change vs previous period',
    viewAll: 'View all',
  },
};

export default en;
